import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { isMobileSidebarLayout, isSidebarVisuallyOpen } from '@/utils/sidebarLayout';
import { useUserDashboardPreferencesStore } from '@/stores/userDashboardPreferences';
import { shouldUseDashboardV2Shell } from '@/utils/dashboardV2Shell';

/**
 * Layout auth/app deve seguir a rota ativa (meta.layout), não o Vuex setado no beforeEach.
 * Shell v2 segue preferência + rota (não troca shell enquanto ainda no Home legado).
 */
export function useLayout() {
  const store = useStore();
  const route = useRoute();
  const dashboardPrefs = useUserDashboardPreferencesStore();
  const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);

  const updateViewportWidth = () => {
    viewportWidth.value = window.innerWidth;
  };

  onMounted(() => {
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    if (store.getters['auth/isAuthenticated']) {
      dashboardPrefs.fetchPreferences().catch(() => {});
    }
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateViewportWidth);
  });

  const layoutState = computed(() => {
    const showSidebar = store.state.is_show_sidebar;
    const isMobileSidebar = isMobileSidebarLayout(viewportWidth.value);
    const isAuth = route.meta?.layout === 'auth';
    const isAuthenticated = !!store.getters['auth/isAuthenticated'];
    // Sem sessão não usa shell V2 (shellHint no localStorage gerava menu quebrado em `/`).
    const isDashboardV2Shell =
      !isAuth &&
      isAuthenticated &&
      shouldUseDashboardV2Shell(dashboardPrefs.isNewDashboardActive, route);
    const hideAppChrome = isAuth || isDashboardV2Shell || !isAuthenticated;

    return {
      isAuth,
      isDashboardV2: isDashboardV2Shell,
      hideAppChrome,
      showSidebar,
      isMobileSidebar,
      isSidebarVisuallyOpen: isSidebarVisuallyOpen(showSidebar, viewportWidth.value),
      sidebarSkipTransition: store.state.sidebar_skip_transition,
      menuStyle: store.state.menu_style,
      showSearch: store.state.is_show_search,
      layoutStyle: store.state.layout_style
    };
  });

  const toggleSidebar = () => {
    const next = !layoutState.value.showSidebar;
    store.commit('toggleSideBar', next);
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      localStorage.setItem('sidebar_state_mobile', String(next));
    }
  };

  const toggleSearch = () => {
    store.commit('toggleSearch', !layoutState.value.showSearch);
  };

  return {
    layoutState,
    toggleSidebar,
    toggleSearch
  };
}
